import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ChevronLeft, Cpu, Loader2, Search, Briefcase, AlertTriangle, TrendingUp, HelpCircle, Sparkles, CheckCircle, Upload, ScanLine, Layers, X, ShieldCheck, Lock 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion'; 
import { useAppContext } from '../../core/App';
import { useCurriculum } from '../../core/context/CurriculumContext';
import ReportCard from './components/ReportCard';
import { initialResumeReports } from '../../core/constants/initialStates';
import mammoth from 'mammoth';

import LongevityReportDisplay from './components/LongevityReportDisplay';
import JobMatchesDisplay from './components/JobMatchesDisplay';
import MissionBriefing from '../../components/shared/MissionBriefing';
import RecruiterVision from './components/RecruiterVision';
import { useResumeEngine } from './hooks/useResumeEngine';

const LOADING_MESSAGES = [
  "Analyzing Job Description...",
  "Scanning Resume for Keywords...",
  "Checking ATS Compatibility...",
  "Measuring Impact Verbs...",
  "Drafting Your Report..."
];

const BRIEFING_DATA = {
  title: "Operation: Resume Rescue",
  subtitle: "Beat the Applicant Tracking System (ATS) and get hired.",
  steps: [
    "Paste a Job Description to see how well you match the role.",
    "Paste your Resume to analyze your keywords and formatting.",
    "Use the AI Feedback to fix 'Red Flags' and boost your score."
  ],
  tips: "Don't just stuff keywords! The AI checks for CONTEXT. Listing 'Python' as a hobby won't count as 'Professional Experience'."
};

const MAX_EXCEL_CHARS = 150000; 

const AIResumeGrader = ({ onComplete, isReviewMode }) => {
  const { 
    authUser,
    userProfile, 
    goToDashboard, 
    callApi, 
    resumeReports 
  } = useAppContext();

  const { saveAiAnalysisToSubCollection } = useCurriculum() || {};
  
  const fileInputRef = useRef(null); 

  const latestReport = useMemo(() => (resumeReports?.length > 0) ? resumeReports[0] : initialResumeReports, [resumeReports]);

  const {
      isLoading, error, setError,
      isProcessing, roleAnalysis, setRoleAnalysis, resumeAnalysis, setResumeAnalysis,
      isFindingJobs, jobMatches, setJobMatches,
      generateInitialReport, finalizeAndSaveReport,
      analyzeRoleRisk, analyzeResumeResilience, findJobMatches
  } = useResumeEngine();

  const [jobDescription, setJobDescription] = useState('');
  const [resume, setResume] = useState('');
  const [reportCard, setReportCard] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);

  const [roleError, setRoleError] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const [jobFinderError, setJobFinderError] = useState(null);

  const [suggestedTitles, setSuggestedTitles] = useState([]); 
  const [isLoadingTitles, setIsLoadingTitles] = useState(false);
  const [isGeneratingSampleJD, setIsGeneratingSampleJD] = useState(false);
  const [showSampleJdMenu, setShowSampleJdMenu] = useState(false);
  
  const [showBriefing, setShowBriefing] = useState(false);

  // --- NEW: DRAG & DROP STATES ---
  const [isDraggingJD, setIsDraggingJD] = useState(false);
  const [isDraggingResume, setIsDraggingResume] = useState(false);

  const [auditPhase, setAuditPhase] = useState(null); 
  const [userHighlights, setUserHighlights] = useState([]);
  const [auditTimeLeft, setAuditTimeLeft] = useState(15);
  const [hasEarnedAuditCxp, setHasEarnedAuditCxp] = useState(false);
  const [tempReportCard, setTempReportCard] = useState(null);

  const [validationStatus, setValidationStatus] = useState(null); 
  const [isValidating, setIsValidating] = useState(false);
  const [showRecruiterVision, setShowRecruiterVision] = useState(false);

  useEffect(() => { 
    if (!isReviewMode) {
        setShowBriefing(true); 
    }
  }, [isReviewMode]);

  useEffect(() => {
    if (isReviewMode && latestReport && (latestReport.overall_score || latestReport.reportCard)) {
      setReportCard(latestReport);
      setJobDescription(latestReport.jobDescription || '');
      if (latestReport.resume) setResume(latestReport.resume);
      if (latestReport.roleAnalysis) setRoleAnalysis(latestReport.roleAnalysis);
      if (latestReport.resumeAnalysis) setResumeAnalysis(latestReport.resumeAnalysis);
      if (latestReport.jobMatches) setJobMatches(latestReport.jobMatches);
    }
  }, [isReviewMode, latestReport, setRoleAnalysis, setResumeAnalysis, setJobMatches]);

  useEffect(() => {
    let interval;
    if (isLoading) {
      let i = 0;
      interval = setInterval(() => { i = (i + 1) % LOADING_MESSAGES.length; setLoadingMessage(LOADING_MESSAGES[i]); }, 1500); 
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  // Stepping by 500ms to guarantee absolute precision at the 7.5s (7500ms) threshold
  useEffect(() => {
    let timer;
    if (auditPhase === 'audit' && auditTimeLeft > 0) {
      timer = setInterval(() => { setAuditTimeLeft((prev) => Math.max(0, prev - 0.5)); }, 500);
    } else if (auditPhase === 'audit' && auditTimeLeft <= 0) {
      setAuditPhase('comparison');
    }
    return () => clearInterval(timer);
  }, [auditPhase, auditTimeLeft]);

  const readWordFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        mammoth.extractRawText({ arrayBuffer: e.target.result })
          .then((result) => resolve(result.value.substring(0, MAX_EXCEL_CHARS)))
          .catch((err) => reject(err));
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  };

  const validateUploadedContent = async (fileText) => {
      setIsValidating(true);
      setValidationStatus(null);
      await new Promise(resolve => setTimeout(resolve, 500)); 

      const cleanText = fileText.replace(/\s/g, '').length;
      
      if (cleanText < 50) {
          setValidationStatus({
              type: 'error',
              msg: 'FILE UNREADABLE: The document appears to be empty or a scanned image. Please upload a native .docx or .txt file.'
          });
          setIsValidating(false);
          return false;
      }

      setValidationStatus({
          type: 'success',
          msg: `PRE-FLIGHT CLEARED: ${cleanText} characters extracted.`
      });
      setIsValidating(false);
      return true;
  };

  // --- REFACTORED: Unified File Processor ---
  const processExtractedFile = async (file, targetSetter) => {
      if (!file) return;
      let extractedText = "";

      try {
          if (file.name.match(/\.(doc|docx)$/i)) {
              extractedText = await readWordFile(file);
          } else if (file.type === "text/plain") {
              extractedText = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = (event) => resolve(event.target.result);
                  reader.onerror = (error) => reject(error);
                  reader.readAsText(file);
              });
          } else {
              setError("Please upload .docx or .txt files. PDFs are often unreadable by ATS systems!");
              return;
          }

          targetSetter(extractedText);
          validateUploadedContent(extractedText);

      } catch (err) {
          console.error("File Read Error:", err);
          setError("Error reading file. Please try a standard text format.");
      }
  };

  const handleFileUpload = (e) => {
      processExtractedFile(e.target.files[0], setResume);
  };

  // --- NEW: DRAG & DROP EVENT HANDLERS ---
  const handleDragOverJD = (e) => { e.preventDefault(); setIsDraggingJD(true); };
  const handleDragLeaveJD = (e) => { e.preventDefault(); setIsDraggingJD(false); };
  const handleDropJD = (e) => {
      e.preventDefault();
      setIsDraggingJD(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          processExtractedFile(e.dataTransfer.files[0], setJobDescription);
      }
  };

  const handleDragOverResume = (e) => { e.preventDefault(); setIsDraggingResume(true); };
  const handleDragLeaveResume = (e) => { e.preventDefault(); setIsDraggingResume(false); };
  const handleDropResume = (e) => {
      e.preventDefault();
      setIsDraggingResume(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          processExtractedFile(e.dataTransfer.files[0], setResume);
      }
  };

  const handleStartGradeProcess = async (e) => {
    if (e) e.preventDefault();
    if (!resume.trim()) { setError('Please paste your resume first.'); return; }
    
    if (validationStatus?.type === 'error') {
        alert("Cannot proceed: File is unreadable.");
        return;
    }

    if (!isReviewMode && !reportCard && auditPhase === null) {
      setAuditPhase('audit');
      setAuditTimeLeft(15);
      setUserHighlights([]);
      setTempReportCard(null);

      try {
        const parsedResult = await generateInitialReport(resume, jobDescription);
        setTempReportCard(parsedResult);
      } catch (err) {
        console.error("Background API Sync Failed:", err);
      }
    } else if (isReviewMode) {
      handleProceedToReport(true); 
    }
  };

  const handleProceedToReport = async (bypassDB = false) => {
    if (!tempReportCard) {
        console.error("❌ ERROR: No temporary report found to save.");
        return;
    }

    try {
      if (bypassDB !== true) {
          await finalizeAndSaveReport(resume, jobDescription, tempReportCard);
      }

      setReportCard(tempReportCard);
      setAuditPhase(null);

    } catch (err) { 
        console.error("🔥 FATAL SAVE CRASH:", err); 
    } 
  };

  const handleRecalibrate = () => {
    setAuditPhase(null);
    setTempReportCard(null);
    setUserHighlights([]);
  };

  const handleFetchJobTitles = async () => {
    if (suggestedTitles.length > 0) { setShowSampleJdMenu(true); return; }
    setIsLoadingTitles(true);
    try {
       const track = userProfile?.careerTrack || "General Business";
       const systemInstruction = `ROLE: Career Market Analyst. TASK: Provide 5 entry-level job titles relevant to the '${track}' sector in 2025. SCHEMA: { "titles": ["String"] }`;
       const res = await callApi({ context: "Market analysis" }, systemInstruction);
       if (res && Array.isArray(res.titles)) { 
           setSuggestedTitles(res.titles); 
           setShowSampleJdMenu(true); 
       }
    } catch (e) { 
        console.error("Intelligence Engine Failed:", e);
        setSuggestedTitles(["Junior Analyst", "Project Coordinator", "Associate"]); 
        setShowSampleJdMenu(true); 
    } finally { setIsLoadingTitles(false); }
  };

  const handleGenerateSampleJD = async (title) => {
    setShowSampleJdMenu(false); 
    setIsGeneratingSampleJD(true); 
    setJobDescription(`Querying market standards for ${title}...`);
    try {
       const systemInstruction = `ROLE: Technical Recruiter. TASK: Write a realistic, 150-word Job Description for a '${title}' role. Focus on typical requirements for a new graduate. SCHEMA: { "job_description_text": "String" }`;
       const res = await callApi({ JOB_TITLE: title }, systemInstruction);
       if (res && res.job_description_text) setJobDescription(res.job_description_text);
    } catch(e) {
       setJobDescription(`Error generating JD. Please paste one manually.`);
    } finally { setIsGeneratingSampleJD(false); }
  };

  const handleAnalyzeRole = async () => {
    if (!jobDescription || isProcessing) return;
    setRoleError(null);
    try {
        await analyzeRoleRisk(jobDescription, latestReport?.id);
    } catch (e) { 
        setRoleError(e.message);
    } 
  };

  const handleAnalyzeResume = async () => {
    if (!resume || isProcessing) return;
    setResumeError(null);
    try {
        await analyzeResumeResilience(resume, latestReport?.id);
    } catch (e) { 
        setResumeError(e.message);
    } 
  };

  const handleFindJobMatches = async () => {
    setJobFinderError(null);
    try {
      await findJobMatches(resume);
    } catch(e) { 
        setJobFinderError(e.message); 
    } 
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pb-20 pt-6">
       
       <AnimatePresence>
          {showRecruiterVision && (
            <RecruiterVision 
                viewMode="standard"
                resumeText={resume} 
                reportData={reportCard} 
                onClose={() => setShowRecruiterVision(false)} 
            />
          )}
          
          {(auditPhase === 'audit' || auditPhase === 'comparison') && (
            <RecruiterVision 
                viewMode={auditPhase}
                resumeText={resume} 
                reportData={tempReportCard} 
                userHighlights={userHighlights}
                setUserHighlights={setUserHighlights}
                timeLeft={auditTimeLeft}
                onProceed={() => handleProceedToReport(false)} 
                onRecalibrate={handleRecalibrate}
                hasEarnedAuditCxp={hasEarnedAuditCxp}
                setHasEarnedAuditCxp={setHasEarnedAuditCxp}
                isReviewMode={isReviewMode}
            />
          )}
       </AnimatePresence>

       <div className="flex items-center mb-6 px-4 sm:px-0">
        <button onClick={goToDashboard} className="p-2 rounded-full bg-white shadow-sm hover:bg-gray-50 transition mr-4"><ChevronLeft className="w-6 h-6 text-gray-600" /></button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center"><Cpu className="w-7 h-7 sm:w-8 sm:h-8 mr-3 text-blue-600" /> AI Resume Grader</h1>
          <p className="text-gray-600 text-sm sm:text-base">Adaptive Curriculum Engine active.</p>
        </div>
        <button onClick={() => setShowBriefing(true)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"><HelpCircle className="w-6 h-6" /></button>
      </div>

      <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 mx-4 sm:mx-0 relative">
        <AnimatePresence>
          {showSampleJdMenu && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="absolute top-24 left-8 right-8 z-20 bg-white border border-blue-200 shadow-2xl rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-black text-blue-900 uppercase tracking-tight flex items-center gap-2"><Sparkles size={18} className="text-blue-500" /> Roles for: {userProfile?.careerTrack || "Target Track"}</h4>
                <button onClick={() => setShowSampleJdMenu(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {suggestedTitles.map((title, idx) => (
                  <button key={idx} onClick={() => handleGenerateSampleJD(title)} className="text-left p-3 rounded-xl border border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition-all font-bold text-sm text-gray-700">{title}</button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {validationStatus && (
            <div className={`p-4 rounded-xl border flex items-start gap-3 mb-6 animate-in fade-in slide-in-from-top-2 ${validationStatus.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                <ScanLine size={20} className="flex-shrink-0 mt-0.5" />
                <div><h4 className="font-bold text-xs uppercase tracking-wider mb-1">Pre-Flight Check</h4><p className="text-sm font-medium">{validationStatus.msg}</p></div>
            </div>
        )}

        <div className="space-y-6">
          <div>
            <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Job Description</label>
                {!isReviewMode && (
                    <button onClick={handleFetchJobTitles} disabled={isLoadingTitles} className="text-xs flex items-center text-blue-600 font-black uppercase tracking-wider hover:text-blue-800 transition-colors">
                        {isLoadingTitles ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} {isLoadingTitles ? "Scanning Market..." : "Use Intelligence Engine"}
                    </button>
                )}
            </div>
            <textarea 
                onDragOver={handleDragOverJD}
                onDragLeave={handleDragLeaveJD}
                onDrop={handleDropJD}
                className={`w-full h-48 p-4 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-y font-mono text-sm transition-colors duration-200 ${isDraggingJD ? 'bg-blue-50 border-blue-400' : 'border-gray-300 bg-white'}`} 
                placeholder="Paste JD or Drag & Drop file here..." 
                value={jobDescription} 
                onChange={(e) => setJobDescription(e.target.value)} 
                disabled={isReviewMode || isLoading} 
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Resume Text</label>
                {!isReviewMode && (
                    <>
                        <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".doc,.docx,.txt" className="hidden" />
                        <button onClick={() => fileInputRef.current.click()} className="text-xs flex items-center text-gray-500 font-bold hover:text-blue-600 transition-colors"><Upload className="w-3 h-3 mr-1" /> Upload File (.docx/.txt)</button>
                    </>
                )}
            </div>
            <textarea 
                onDragOver={handleDragOverResume}
                onDragLeave={handleDragLeaveResume}
                onDrop={handleDropResume}
                className={`w-full h-96 p-4 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 resize-y transition-colors duration-200 ${isDraggingResume ? 'bg-blue-50 border-blue-400' : 'border-gray-300 bg-white'}`} 
                placeholder="Paste resume or Drag & Drop file here..." 
                value={resume} 
                onChange={(e) => setResume(e.target.value)} 
                disabled={isReviewMode || isLoading} 
            />
          </div>

          {!isReviewMode && (
            <button onClick={handleStartGradeProcess} disabled={isLoading || !resume.trim() || validationStatus?.type === 'error'} className={`w-full flex items-center justify-center font-black py-4 px-6 rounded-xl shadow-lg transition duration-300 disabled:opacity-50 bg-blue-600 hover:bg-blue-700 text-white`}>
              {isLoading ? <><Loader2 className="animate-spin mr-2" />{loadingMessage}</> : 'Start 15-Second Self-Audit'}
            </button>
          )}
        </div>
        
        {error && <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center"><AlertTriangle className="w-5 h-5 mr-2" />{error}</div>}

        {reportCard && (
          <div className="mt-10 animate-fade-in relative">
            {/* UNGATED BRUTAL SCORE REVEAL */}
            {!authUser && (
                <div className="mb-6 p-6 md:p-8 border border-red-200 rounded-2xl shadow-lg bg-red-50 relative overflow-hidden">
                    <div className="flex flex-col md:flex-row items-center justify-between relative z-10">
                      <div className="text-center md:text-left mb-4 md:mb-0">
                        <h2 className="text-2xl font-black text-red-900 tracking-tight">ATS Reality Check</h2>
                        <p className="text-red-700 mt-1 max-w-sm text-sm font-medium">
                            Your resume was evaluated against enterprise ATS algorithms. This is your raw viability score.
                        </p>
                      </div>
                      <div className="text-center">
                        <div className={`text-6xl font-black ${reportCard.overall_score >= 80 ? 'text-green-600' : reportCard.overall_score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{reportCard.overall_score}</div>
                        <div className="text-xs font-bold text-red-500 uppercase tracking-wider mt-1">Viability Score</div>
                      </div>
                    </div>
                </div>
            )}

            {/* GATED REPORT SECTION */}
            <div className="relative">
                {!authUser && (
                   <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-md rounded-2xl mt-4">
                     <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md text-center border border-slate-200">
                        <Lock className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                        <h3 className="text-2xl font-black text-slate-900 mb-2">Detailed Analysis Locked</h3>
                        <p className="text-slate-600 mb-6 font-medium">To unlock your verified data, correct your skill gaps, and access the Survival Gauntlet, connect via Google Sign-On.</p>
                        <button onClick={() => goToDashboard()} className="w-full bg-blue-600 text-white font-black py-4 rounded-xl shadow-lg hover:bg-blue-700 transition">Unlock Full Report</button>
                     </div>
                   </div>
                )}
                
                <div className={!authUser ? "opacity-30 blur-md pointer-events-none select-none pt-4" : "pt-4"}>
                    <div className="mb-8 p-6 bg-slate-900 rounded-2xl text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-4 border border-slate-700">
                       <div>
                          <h3 className="text-lg font-black uppercase tracking-widest flex items-center gap-2"><Layers className="text-cyan-400" /> Recruiter Vision</h3>
                          <p className="text-slate-400 text-sm mt-1">See your resume through the eyes of a 6-second skim.</p>
                       </div>
                       <button 
                         onClick={() => setShowRecruiterVision(true)}
                         className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-black rounded-xl shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center gap-2"
                       >
                         <ScanLine size={18} /> Activate Heatmap
                       </button>
                    </div>

                    <ReportCard report={reportCard} hideScoreHeader={!authUser} />
                    
                    <div className="mt-8 space-y-4">
                        <div className="p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-start"><Search className="w-6 h-6 text-yellow-600 mr-3 mt-1" /><div><h4 className="font-bold text-yellow-800">Explore Career Options</h4><p className="text-sm text-yellow-700">Find modern roles that match your current skills.</p></div></div>
                            <button onClick={handleFindJobMatches} disabled={isFindingJobs} className="bg-yellow-600 text-white px-6 py-3 rounded-xl font-bold flex items-center shadow-lg">{isFindingJobs ? <Loader2 className="animate-spin mr-2"/> : <Search className="mr-2"/>} Find Matches</button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                        <button 
                            onClick={handleAnalyzeRole} 
                            disabled={isProcessing} 
                            className="p-6 bg-slate-900 border-l-4 border-cyan-500 rounded-r-2xl text-left hover:bg-slate-800 transition shadow-lg"
                        >
                            <div className="flex items-center gap-3 mb-2">
                                {isProcessing ? <Loader2 className="animate-spin text-cyan-400" /> : <TrendingUp className="text-cyan-400" />}
                                <h4 className="font-bold text-white uppercase text-xs tracking-widest">Market Risk</h4>
                            </div>
                            <p className="text-slate-300 text-sm font-bold">Analyze the ROLE for automation risk</p>
                        </button>

                        <button 
                            onClick={handleAnalyzeResume} 
                            disabled={isProcessing} 
                            className="p-6 bg-blue-900 border-l-4 border-blue-400 rounded-r-2xl text-left hover:bg-blue-800 transition shadow-lg"
                        >
                            <div className="flex items-center gap-3 mb-2">
                                {isProcessing ? <Loader2 className="animate-spin text-blue-300" /> : <ShieldCheck className="text-blue-300" />}
                                <h4 className="font-bold text-white uppercase text-xs tracking-widest">Personal Armor</h4>
                            </div>
                            <p className="text-slate-100 text-sm font-bold">Analyze YOUR Resume for resilience</p>
                        </button>
                    </div>
                    
                    {jobFinderError && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg">{jobFinderError}</div>}
                    <JobMatchesDisplay matches={jobMatches} />
                    
                    {roleError && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg">{roleError}</div>}
                    {roleAnalysis && <LongevityReportDisplay report={roleAnalysis} type="role" />}
                    
                    {resumeError && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg">{resumeError}</div>}
                    {resumeAnalysis && <LongevityReportDisplay report={resumeAnalysis} type="resume" />}
                    
                    {!isReviewMode && authUser && (
                        <div className="mt-12 text-center pt-8 border-t border-gray-100">
                            <button onClick={onComplete} className="px-10 py-5 bg-green-600 text-white font-black rounded-2xl shadow-2xl hover:scale-105 transition flex items-center justify-center mx-auto"><CheckCircle className="w-6 h-6 mr-3" /> Complete & Return to Dashboard</button>
                        </div>
                    )}
                </div>
            </div>
          </div>
        )}
      </div>
      <MissionBriefing isOpen={showBriefing} onClose={() => setShowBriefing(false)} {...BRIEFING_DATA} />
    </div>
  );
};

export default AIResumeGrader;